<template>
  <div style="height: 90vh;">
    <PeopleFilters
      v-if="showFilters"
      :filters.sync="filters"
      :search.sync="search"
      :filter-groups-config="filterGroupsConfig"
      class="mb-3"
    />
    <div
      v-if="totalItems > 0 || hasActiveFilters"
      style="height: calc(90vh - 120px); overflow-y: auto;"
    >
      <v-col class="mt-0 pt-0">
        <PeopleTable
          :headers="headers"
          :items="rows"
          :page.sync="page"
          :page-count="pageCount"
          :total="totalItems"
          :items-per-page.sync="itemsPerPage"
          :items-per-page-options="itemsPerPageOptions"
          :is-dark="$vuetify.theme.dark"
          @row-click="goToOperator"
        />
      </v-col>
    </div>
    <v-row
      v-else-if="showEmptyState"
      class="fill-height"
      align="center"
      justify="center"
      style="min-height: 70vh;"
    >
      <v-col cols="12" md="6" lg="4">
        <EmptyStateButton
          :items="rows"
          title="Operator Groups"
          message="List of all operator groups will be shown here."
          button-text="Create operator group"
          @create="createOperatorGroup"
        />
      </v-col>
    </v-row>

    <FlyoutMenu/>
    <Alert />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Alert from '../components/Alert';
import EmptyStateButton from '../components/Accounts/EmptyStateButton.vue';
import PeopleFilters from '../components/Accounts/PeopleFilters.vue';
import PeopleTable from '../components/Accounts/PeopleTable.vue';
import FlyoutMenu from '../components/FlyoutMenu.vue';

export default {
  name: 'OperatorGroups',
  components: { Alert, PeopleTable, PeopleFilters, EmptyStateButton, FlyoutMenu },

  data() {
    return {
      search: '',
      hadGroups: false,
      filters: { active: [], automatic: [] },
      filterGroupsConfig: [
        { key: 'active',    label: 'Status',    options: ['Active', 'Inactive'] },
        { key: 'automatic', label: 'Automatic', options: ['Yes', 'No'] },
      ],
      page: 1,
      itemsPerPage: 36,
      itemsPerPageOptions: [12, 24, 36, 72, 120],
      debounceTimer: null,
      headers: [
        { text: 'ID', value: 'id' },
        { text: 'Name', value: 'name' },
        { text: 'Description', value: 'description' },
        { text: 'Active', value: 'active' },
        { text: 'Operators', value: 'number_of_operators' },
        { text: 'Type', value: 'type' },
        { text: 'Automatic Group', value: 'automatic' },
        { text: 'Created By', value: 'created_by_email' },
        { text: 'Created', value: 'created_at' },
        { text: 'Updated', value: 'updated_at' },
      ],
    };
  },

  computed: {
    ...mapGetters({
      groups:     'operators/getGroups',
      groupsMeta: 'operators/getGroupsMeta',
      operatorsLoading: 'operators/isLoading',
    }),

    // Hidden only for a space with no groups at all, so the create-group
    // empty state stays alone as before.
    showFilters() {
      return this.totalItems > 0 || this.hasActiveFilters || this.hadGroups;
    },
    // Never flash the create-group state while the first fetch is in flight.
    showEmptyState() {
      return !this.operatorsLoading.groups;
    },

    rows() {
      return (this.groups || []).map(g => ({
        id:                 g.id,
        uuid:               g.uuid,
        name:               g.name,
        description:        g.description,
        type:               g.type || 'Manual',
        automatic:          g.automatic_operator_group ? 'Yes' : 'No',
        active:             g.active ? 'Active' : 'Inactive',
        number_of_operators: g.operators_number ?? 0,
        created_by_email:   g.created_by_email,
        created_at:         this.formatDate(g.created_at),
        updated_at:         this.formatDate(g.updated_at),
      }));
    },

    totalItems() {
      return this.groupsMeta?.count || 0;
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.totalItems / this.itemsPerPage));
    },

    hasActiveFilters() {
      return this.search.trim() !== '' ||
        Object.values(this.filters).some(arr => arr.length > 0);
    },

    queryParams() {
      const params = {
        page:      this.page,
        page_size: this.itemsPerPage,
      };

      // active: only when a single option is selected (same pattern as Operators status)
      if (this.filters.active.length === 1) {
        params.active = this.filters.active[0] === 'Active';
      }
      if (this.filters.automatic.length === 1) {
        params.automatic = this.filters.automatic[0] === 'Yes';
      }
      if (this.search.trim()) {
        params.search = [this.search.trim()];
      }

      return params;
    },
  },

  watch: {
    // once a space is known to have groups, keep the filter bar visible
    totalItems(val) {
      if (val > 0) this.hadGroups = true;
    },

    filters:      { deep: true, handler() { this.page = 1; } },
    search()      { this.page = 1; },
    itemsPerPage(){ this.page = 1; },

    queryParams: {
      deep: true,
      handler() {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => this.fetchGroups(this.queryParams), 700);
      },
    },
  },

  methods: {
    ...mapActions({ fetchGroups: 'operators/fetchOperatorGroups' }),

    formatDate(iso) {
      if (!iso) return '-';
      const d = new Date(iso);
      const pad = n => String(n).padStart(2, '0');
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },

    createOperatorGroup() {
      this.$router.push({ name: 'addOperatorGroup' });
    },

    goToOperator(row) {
      if (!row?.uuid) return;
      this.$router.push({ name: 'viewOperatorGroup', params: { uuid: row.uuid, action: 'view' } });
    },
  },

  mounted() {
    this.fetchGroups(this.queryParams);
  },
};
</script>
